import React, { useState, useRef, useEffect, useMemo } from 'react';

function ProjectSelect({ projects, value, onChange, placeholder = 'Select project...' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Build tree structure
  const tree = useMemo(() => {
    const projectMap = new Map();
    const roots = [];

    projects.forEach(p => projectMap.set(p.id, { ...p, children: [] }));

    projects.forEach(p => {
      const project = projectMap.get(p.id);
      if (p.parent) {
        const parent = projectMap.get(p.parent.id);
        if (parent) parent.children.push(project);
        else roots.push(project);
      } else {
        roots.push(project);
      }
    });

    const sortProjects = (arr) => {
      arr.sort((a, b) => a.name.localeCompare(b.name));
      arr.forEach(p => sortProjects(p.children));
    };
    sortProjects(roots);
    return roots;
  }, [projects]);

  // Filter tree by search
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return tree;
    const query = searchQuery.toLowerCase();

    const filterProject = (project) => {
      const matches = project.name.toLowerCase().includes(query) ||
                      project.identifier?.toLowerCase().includes(query);
      const filteredChildren = project.children.map(filterProject).filter(Boolean);

      if (matches || filteredChildren.length > 0) {
        return { ...project, children: filteredChildren, _matched: matches };
      }
      return null;
    };

    return tree.map(filterProject).filter(Boolean);
  }, [tree, searchQuery]);

  // Auto-expand all when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const ids = new Set();
      const collect = (arr) => arr.forEach(p => {
        if (p.children.length > 0) { ids.add(p.id); collect(p.children); }
      });
      collect(filteredTree);
      setExpandedIds(ids);
    }
  }, [searchQuery, filteredTree]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  const toggleExpand = (e, projectId) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const handleSelect = (projectId) => {
    onChange(String(projectId));
    setIsOpen(false);
    setSearchQuery('');
  };

  // Find selected project name
  const selectedProject = projects.find(p => String(p.id) === String(value));

  const renderNode = (project, depth = 0) => {
    const hasChildren = project.children.length > 0;
    const isExpanded = expandedIds.has(project.id);
    const isSelected = String(project.id) === String(value);

    return (
      <div key={project.id}>
        <div
          className={`ps-option ${isSelected ? 'selected' : ''} ${project._matched ? 'matched' : ''}`}
          style={{ paddingLeft: `${depth * 18 + 12}px` }}
          onClick={() => handleSelect(project.id)}
        >
          {hasChildren ? (
            <button
              className="ps-expand"
              onClick={(e) => toggleExpand(e, project.id)}
              type="button"
            >
              {isExpanded ? '\u25BC' : '\u25B6'}
            </button>
          ) : (
            <span className="ps-expand-spacer" />
          )}
          <span className="ps-name">{project.name}</span>
          <span className="ps-identifier">{project.identifier}</span>
          {isSelected && (
            <svg className="ps-check" width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <path d="M5.5 10.5L2 7l1-1 2.5 2.5L11 3l1 1-6.5 6.5z"/>
            </svg>
          )}
        </div>
        {hasChildren && isExpanded && project.children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className={`project-select ${isOpen ? 'open' : ''}`} ref={containerRef}>
      <div
        className="custom-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="custom-select-value">
          {selectedProject ? selectedProject.name : placeholder}
        </span>
        <span className="custom-select-arrow">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 8L2 4h8L6 8z"/>
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="ps-dropdown">
          <div className="ps-search-wrap">
            <input
              ref={searchRef}
              type="text"
              className="ps-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter projects..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="ps-list">
            <div
              className={`ps-option ps-placeholder ${!value ? 'selected' : ''}`}
              onClick={() => handleSelect('')}
            >
              <span className="ps-name">{placeholder}</span>
            </div>
            {filteredTree.length === 0 ? (
              <div className="ps-empty">No projects found</div>
            ) : (
              filteredTree.map(p => renderNode(p))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectSelect;
